import React, { useState, useEffect } from 'react'
import { Product } from '../services/api'

// Evento global para que al accionar una tarjeta se limpien las demás
const RESET_EVENT = 'jenny_kids_talla_reset'

interface ProductCardProps {
  product: Product
  whatsappNumber: string
}

const ALL_SIZES = ['4', '6', '8', '10', '12', '14', '16', 'S']

const ProductCard: React.FC<ProductCardProps> = ({ product, whatsappNumber }) => {
  const [selectedSize, setSelectedSize] = useState<string | null>(null)
  const [imgIndex, setImgIndex] = useState(0)

  // Escuchar cuando otra tarjeta fue accionada — limpiar la talla de esta
  useEffect(() => {
    const resetHandler = (e: Event) => {
      const { exceptId } = (e as CustomEvent<{ exceptId: string }>).detail
      if (exceptId !== product.id) setSelectedSize(null)
    }
    window.addEventListener(RESET_EVENT, resetHandler)
    return () => window.removeEventListener(RESET_EVENT, resetHandler)
  }, [product.id])

  // Construir lista de imágenes: principal primero, luego extras
  const mainUrl = product.full_image_url || product.image_url || ''
  const extras = product.extra_images || []

  // Ordenar: primaria al frente, luego el resto
  const primaryExtra = extras.find(i => i.is_primary)
  const otherExtras  = extras.filter(i => !i.is_primary)
  const allImages: string[] = primaryExtra
    ? [primaryExtra.full_url, ...otherExtras.map(i => i.full_url), ...(mainUrl ? [mainUrl] : [])]
    : [mainUrl, ...extras.map(i => i.full_url)]

  // Deduplicar y eliminar vacíos
  const images = [...new Set(allImages.filter(Boolean))]

  const stock = (size: string): number => product.stock_by_size[size] ?? 0
  const isAvailable = (size: string): boolean => stock(size) > 0 && product.available_sizes.includes(size)

  const dispatchReset = () => {
    window.dispatchEvent(new CustomEvent(RESET_EVENT, { detail: { exceptId: product.id } }))
  }

  const handleWhatsApp = () => {
    if (!selectedSize) return
    dispatchReset()
    const message = `Hola Jenny Kids, me interesa el producto: *${product.name}*, talla *${selectedSize}*, precio *$${product.price_cop.toLocaleString('es-CO')}*`
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank')
  }

  const handlePedirConDatos = () => {
    if (!selectedSize) return
    dispatchReset()
    sessionStorage.setItem('jenny_kids_producto', JSON.stringify({
      nombre: product.name,
      talla: selectedSize,
      precio: product.price_cop,
    }))
    // Notifica a ContactForm (que ya está montado) que hay un producto nuevo
    window.dispatchEvent(new Event('jenny_kids_producto_set'))
    document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })
  }

  const availableSizesArray = ALL_SIZES.filter(s => product.available_sizes.includes(s))
  const currentImg = images[imgIndex] || 'https://via.placeholder.com/300x400/CCCCCC/666666?text=Sin+imagen'

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">

      {/* Imagen con carrusel */}
      <div className="aspect-[3/4] overflow-hidden bg-gray-100 relative group">
        <img
          src={currentImg}
          alt={product.name}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x400/CCCCCC/666666?text=Sin+imagen'
          }}
        />

        {/* Flechas navegación (solo si hay más de 1 imagen) */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => setImgIndex(i => (i - 1 + images.length) % images.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-70 text-white w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm font-bold"
            >‹</button>
            <button
              onClick={() => setImgIndex(i => (i + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-70 text-white w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm font-bold"
            >›</button>

            {/* Dots indicadores */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setImgIndex(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${i === imgIndex ? 'bg-white scale-125' : 'bg-white bg-opacity-60'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-gray-800 text-lg truncate" title={product.name}>
          {product.name}
        </h3>

        {product.description && (
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
        )}

        <p className="text-xl font-bold text-green-600 mt-2">
          ${product.price_cop.toLocaleString('es-CO')}
        </p>

        {/* Tallas + botón siempre al fondo del card */}
        <div className="mt-auto pt-3">
          {/* Selector de tallas */}
          {availableSizesArray.length > 0 && (
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-700 block mb-2">Selecciona una talla</label>
              <div className="flex flex-wrap gap-2">
                {availableSizesArray.map((size) => {
                  const available = isAvailable(size)
                  return (
                    <button
                      key={size}
                      onClick={() => {
                      const next = selectedSize === size ? null : size
                      setSelectedSize(next)
                      // Al elegir una talla, limpiar la selección en las demás tarjetas
                      if (next !== null) {
                        window.dispatchEvent(new CustomEvent(RESET_EVENT, { detail: { exceptId: product.id } }))
                      }
                    }}
                      disabled={!available}
                      className={`px-3 py-1 rounded border text-sm font-medium transition-all ${
                        selectedSize === size
                          ? 'bg-black text-white border-black'
                          : available
                            ? 'border-gray-300 text-gray-700 hover:border-black hover:text-black'
                            : 'border-gray-200 text-gray-400 line-through cursor-not-allowed opacity-50'
                      }`}
                      title={!available ? 'Agotado' : `Talla ${size}`}
                    >
                      {size}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* CTA WhatsApp directo */}
          <button
            onClick={handleWhatsApp}
            disabled={!selectedSize || availableSizesArray.length === 0}
            className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12c0 1.54.36 3 .97 4.29L2 22l6.29-.97c1.29.61 2.75.97 4.29.97 5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18c-1.41 0-2.73-.36-3.88-.97l-.28-.15-2.89.44.44-2.89-.15-.28C4.36 14.73 4 13.41 4 12c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8zm3.71-8.04c-.2-.1-1.18-.58-1.37-.65-.19-.07-.33-.1-.47.1-.13.2-.52.65-.63.78-.12.13-.23.15-.43.05-.2-.1-.84-.31-1.6-.99-.59-.53-.99-1.19-1.1-1.39-.12-.2-.01-.31.09-.41.09-.09.2-.23.3-.35.1-.12.13-.2.2-.33.07-.13.04-.25-.02-.35-.07-.1-.47-1.13-.64-1.55-.17-.41-.33-.36-.47-.37-.12 0-.25-.01-.39-.01-.13 0-.33.05-.5.25-.18.2-.66.64-.66 1.56 0 .92.68 1.81.77 1.94.09.13 1.27 1.93 3.06 2.71.43.19.76.3.99.38.43.13.82.11 1.13.07.35-.05 1.06-.43 1.21-.85.15-.42.15-.78.1-.85-.04-.07-.15-.11-.31-.2z" />
            </svg>
            {selectedSize ? `Consultar talla ${selectedSize}` : 'Selecciona una talla'}
          </button>

          {/* CTA secundario — pedido con datos de envío */}
          <button
            onClick={handlePedirConDatos}
            disabled={!selectedSize || availableSizesArray.length === 0}
            className="w-full border-2 border-black text-black hover:bg-black hover:text-white disabled:border-gray-300 disabled:text-gray-400 py-2.5 rounded-lg font-semibold transition-colors duration-200 text-sm mt-2"
          >
            {selectedSize ? `Pedir con datos de envío` : 'Selecciona una talla primero'}
          </button>

          {selectedSize && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              {stock(selectedSize)} disponible{stock(selectedSize) !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductCard
